package org.example.project.planner

import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.padding
import androidx.compose.material3.FloatingActionButton
import androidx.compose.material3.Icon
import androidx.compose.material3.Scaffold
import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier
import org.example.project.client.model.ActivityResponse
import org.example.project.planner.components.AddBlockTypeDialog
import org.example.project.planner.components.BlockEditorDialog
import org.example.project.planner.components.CatalogPickerDialog
import org.example.project.planner.components.EventDetailView
import org.example.project.planner.components.EventListView
import org.example.project.planner.components.FavoritesPickerDialog
import org.example.project.planner.components.PlannerHeader
import org.example.project.planner.components.PlannerStatusBanners
import org.example.project.planner.components.TimePickerDialog
import org.example.project.ui.icons.AppIcons
import org.example.project.ui.theme.PlayfulColors

/**
 * Presentational screen displaying the event planner, timeline, blocks,
 * dialogs, and event editing forms.
 * Clean orchestrator composable: composed of modular components.
 */
@Composable
fun PlannerScreen(
    state: PlannerUiState,
    onOpenEventList: () -> Unit,
    onBackFromEventList: () -> Unit = {},
    onStartCreateEvent: () -> Unit,
    onOpenEventDetail: (String) -> Unit,
    onNameChange: (String) -> Unit,
    onDateChange: (String) -> Unit,
    onStartTimeChange: (String) -> Unit,
    onEndTimeChange: (String) -> Unit,
    onOpenAddBlockDialog: () -> Unit,
    onOpenEditBlockDialog: (BlockDraft) -> Unit,
    onCloseBlockDialog: () -> Unit,
    onEditingBlockChange: (BlockDraft) -> Unit,
    onSaveBlock: (BlockDraft) -> Unit,
    onDeleteBlock: (String) -> Unit,
    onOpenFavoritesPicker: () -> Unit,
    onCloseFavoritesPicker: () -> Unit,
    onSelectFavoriteActivity: (ActivityResponse) -> Unit,
    onSaveEvent: () -> Unit,
    onReloadFromServer: () -> Unit,
    onDeleteEvent: () -> Unit,
    onShiftBlock: (blockId: String, deltaMinutes: Int) -> Unit = { _, _ -> },
    onResizeBlockStart: (blockId: String, deltaMinutes: Int) -> Unit = { _, _ -> },
    onResizeBlockEnd: (blockId: String, deltaMinutes: Int) -> Unit = { _, _ -> },
    onOpenStartTimePicker: () -> Unit = {},
    onOpenEndTimePicker: () -> Unit = {},
    onCloseTimePicker: () -> Unit = {},
    onSelectTimeFromPicker: (String) -> Unit = {},
    onOpenAddBlockTypePicker: () -> Unit = {},
    onCloseAddBlockTypePicker: () -> Unit = {},
    onAddBlockAt: (startTime: String, endTime: String) -> Unit = { _, _ -> },
    onOpenCustomGameEditor: () -> Unit = {},
    onAddQuickOtherBlock: (String) -> Unit = {},
    onOpenCatalogPicker: () -> Unit = {},
    onCloseCatalogPicker: () -> Unit = {},
    onSearchCatalog: (String) -> Unit = {},
    onSelectCatalogActivity: (ActivityResponse) -> Unit = {},
    onToggleTimelineEditMode: () -> Unit = {},
    onSelectBlock: (String?) -> Unit = {},
    modifier: Modifier = Modifier
) {
    Scaffold(
        modifier = modifier.fillMaxSize(),
        containerColor = PlayfulColors.Background,
        topBar = {
            val currentEventId = (state.viewMode as? PlannerViewMode.EventDetail)?.eventId ?: state.currentEvent?.id
            PlannerHeader(
                viewMode = state.viewMode,
                currentEventName = state.eventDraft.name,
                currentEventId = currentEventId,
                events = state.events,
                isSaving = state.saveStatus is SaveStatus.Saving,
                isTimelineEditMode = state.isTimelineEditMode,
                onToggleTimelineEditMode = onToggleTimelineEditMode,
                onBackToList = onOpenEventList,
                onReturnToPlan = onBackFromEventList,
                onStartCreateEvent = onStartCreateEvent,
                onSelectEvent = onOpenEventDetail,
                onSaveEvent = onSaveEvent,
                onNameChange = onNameChange,
                onDeleteEvent = onDeleteEvent,
                eventStartTime = state.eventDraft.startTime,
                eventEndTime = state.eventDraft.endTime,
                onOpenStartTimePicker = onOpenStartTimePicker,
                onOpenEndTimePicker = onOpenEndTimePicker
            )
        },
        floatingActionButton = {
            if (state.viewMode is PlannerViewMode.CreateEvent || state.viewMode is PlannerViewMode.EventDetail) {
                FloatingActionButton(
                    onClick = onOpenAddBlockTypePicker,
                    containerColor = PlayfulColors.Coral,
                    contentColor = PlayfulColors.TextOnPrimary
                ) {
                    Icon(
                        imageVector = AppIcons.Add,
                        contentDescription = "Dodaj punkt programu"
                    )
                }
            }
        }
    ) { innerPadding ->
        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(innerPadding)
        ) {
            // Status and Feedback Banners
            PlannerStatusBanners(
                saveStatus = state.saveStatus,
                validationError = state.validationError,
                generalError = state.generalError,
                onRetrySave = onSaveEvent,
                onReloadFromServer = onReloadFromServer
            )

            // Main Content by ViewMode
            when (state.viewMode) {
                PlannerViewMode.EventList -> {
                    EventListView(
                        isLoading = state.isLoading,
                        events = state.events,
                        onEventClick = onOpenEventDetail,
                        onCreateClick = onStartCreateEvent
                    )
                }
                PlannerViewMode.CreateEvent, is PlannerViewMode.EventDetail -> {
                    EventDetailView(
                        isCreate = state.viewMode is PlannerViewMode.CreateEvent,
                        draft = state.eventDraft,
                        isSaving = state.saveStatus is SaveStatus.Saving,
                        isTimelineEditMode = state.isTimelineEditMode,
                        selectedBlockId = state.selectedBlockId,
                        onSelectBlock = onSelectBlock,
                        onNameChange = onNameChange,
                        onDateChange = onDateChange,
                        onStartTimeChange = onStartTimeChange,
                        onEndTimeChange = onEndTimeChange,
                        onOpenAddBlockTypePicker = onOpenAddBlockTypePicker,
                        onAddBlockAt = onAddBlockAt,
                        onEditBlockClick = onOpenEditBlockDialog,
                        onDeleteBlockClick = onDeleteBlock,
                        onShiftBlock = onShiftBlock,
                        onResizeBlockStart = onResizeBlockStart,
                        onResizeBlockEnd = onResizeBlockEnd,
                        onSaveClick = onSaveEvent,
                        onDeleteEventClick = onDeleteEvent
                    )
                }
            }
        }
    }

    // Block Editor Dialog
    if (state.isBlockEditorOpen && state.editingBlock != null) {
        val isNewBlock = state.eventDraft.blocks.none { it.id == state.editingBlock.id }
        BlockEditorDialog(
            draft = state.editingBlock,
            isNewBlock = isNewBlock,
            onDraftChange = onEditingBlockChange,
            onSave = { onSaveBlock(state.editingBlock) },
            onDismiss = onCloseBlockDialog,
            onOpenFavoritesPicker = onOpenFavoritesPicker
        )
    }

    // Favorites Picker Dialog
    if (state.isFavoritesPickerOpen) {
        FavoritesPickerDialog(
            favorites = state.favorites,
            onSelect = onSelectFavoriteActivity,
            onDismiss = onCloseFavoritesPicker
        )
    }

    // Catalog Picker Dialog
    if (state.isCatalogPickerOpen) {
        CatalogPickerDialog(
            activities = state.catalogActivities,
            isLoading = state.isCatalogLoading,
            onSearch = onSearchCatalog,
            onSelect = onSelectCatalogActivity,
            onOpenFavorites = onOpenFavoritesPicker,
            onDismiss = onCloseCatalogPicker
        )
    }

    // Add Block Type Dialog
    if (state.isAddTypePickerOpen) {
        AddBlockTypeDialog(
            startTime = state.pendingBlockStartTime,
            endTime = state.pendingBlockEndTime,
            onSelectCatalog = onOpenCatalogPicker,
            onSelectCustom = onOpenCustomGameEditor,
            onSelectOther = onAddQuickOtherBlock,
            onDismiss = onCloseAddBlockTypePicker
        )
    }

    // Time Picker Dialog
    if (state.isTimePickerOpen && state.timePickerTarget != null) {
        val curTime = if (state.timePickerTarget == TimePickerTarget.EVENT_START) {
            state.eventDraft.startTime
        } else {
            state.eventDraft.endTime
        }
        TimePickerDialog(
            target = state.timePickerTarget,
            currentTime = curTime,
            onConfirm = onSelectTimeFromPicker,
            onDismiss = onCloseTimePicker
        )
    }
}
